import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import ConnectIntegrationButton from "../../../common/button/connectintegrationbutton.component.jsx"
import ProductSelect from "../../../common/product-selection/selection.component.jsx"
import { Intl } from "../../../state/helpers/IntlProps"
import { INTEGRATION_KEY } from "../../../state/integration/Constants"
import IntegrationConfigurationValue from "../../../state/integration/IntegrationConfigurationValue"
import {
    FeatureInvoiceCronImport,
    FeaturePurchaseOrderTracking
} from "../../../state/integration/IntegrationFeatureKeys"
import { VEND_FEATURE_SETTINGS } from "../../../state/integration/IntegrationFeatureSettings"
import ConfigurationValueComponent from "../ConfigurationValueComponent"
import FeatureToggleComponent from "../FeatureToggleComponent"

type OwnProps = {
    integrationConfigurationId: string,
    configuration: object,
    configurationValues: IntegrationConfigurationValue[],
    enabledFeatures: object,
    onFeatureToggled: (featureKey: string) => void,
    onConfigurationChange: (configuration: object) => void,
    toggleSaveButtonStatus: (enabled: boolean) => void,
    intl: Intl,
}

const VendFormComponent = (props: OwnProps) => {
    const [quickProductId] = useState(props.configuration["quickProductExternalId"])
    const [productId, setProductId] = useState("")
    useEffect(() => {
        props.toggleSaveButtonStatus(isSaveEnabled(props.configuration))
    }, [props.configuration])

    useEffect(() => {        
        if (quickProductId != "") {
            AppContext.getApiClient().get(`/${INTEGRATION_KEY.VEND}/products/${quickProductId}`).then(res => {
                if (res.id) {
                    setProductId(res.id)
                }
            })
        }
    }, [])

    if (!props.integrationConfigurationId) {
        const url = AppContext.getOptions().apiUrl + "/openid/connect/vend/" +
            AppContext.getUserLoggedIn().id + "?r=/integrations/listing?open=vend"
        return <div className="col-12 ui-module">
            <div className="form-group row">
                <label className="col-3 col-form-label text-align-right">
                    <FormattedMessage
                        id="integration.form.edit.vend.connect"
                        defaultMessage="Connect"
                    />
                </label>
                <div className="col-9">
                    <ConnectIntegrationButton integrationKey={INTEGRATION_KEY.VEND} url={url} />
                </div>
            </div>
        </div>
    }

    return <div className="col-12 ui-module">
        <div className="form-group row">
            <label className="col-3 col-form-label text-align-right" htmlFor="realmId">
                <FormattedMessage
                    id="integration.form.edit.vend.account_id"
                    defaultMessage="Account ID"
                />
            </label>
            <div className="col-9">
                <input
                    name="accountId"
                    value={props.configuration["accountId"]}
                    disabled={true}
                    id="accountId"
                    type="text"
                    className="form-control"
                />
            </div>
        </div>
        <div className="form-group row">
            <label className="col-3 col-form-label text-align-right" htmlFor="name">
                <FormattedMessage
                    id="integration.quick_product.form.edit.name_label"
                    defaultMessage="Product for exporting quick product line items"
                />
            </label>
            <div className="col-9 align-items-center">
                <ProductSelect
                    multi={false}
                    value={productId}
                    onChange={(prod) => {
                        setProductId(prod)
                        const conf = props.configuration
                        AppContext.getApiClient().get("/products/" + prod).then(res => {
                            if (res.attributes.externalLinks) {
                                const externalLink = res.attributes.externalLinks.find(el =>
                                    el.attributes.integrationKey == INTEGRATION_KEY.VEND
                                )
                                if (externalLink) {
                                    const externalId = externalLink.attributes.externalId
                                    conf["quickProductExternalId"] = externalId
                                    props.toggleSaveButtonStatus(isSaveEnabled(conf))
                                    props.onConfigurationChange(conf)
                                }
                            }
                        })
                    }}
                    integrationKey={INTEGRATION_KEY.VEND}
                    clearable={false}
                />
            </div>
        </div>
        {renderFeatureToggles(props)}
        {renderSettingToggles(props)}
        {renderConfigurationValues(props)}
    </div>
}

const renderFeatureToggles = (props: OwnProps) => {
    return Object.keys(props.enabledFeatures).map((featureKey, i) => {
        if (![FeaturePurchaseOrderTracking, FeatureInvoiceCronImport].includes(featureKey)) {
            return
        }

        return <FeatureToggleComponent
            key={i}
            featureKey={featureKey}
            checked={props.enabledFeatures[featureKey]}
            onFeatureToggled={() => { props.onFeatureToggled(featureKey) }}
            renderFeatureLabel={renderFeatureLabel}
        />
    })
}

const renderFeatureLabel = (key: string) => {
    switch (key) {
    case FeaturePurchaseOrderTracking:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.purchase_order_tracking_description"
            defaultMessage="Create and track PO from orders"
        />
    case FeatureInvoiceCronImport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.invoice_sync"
            defaultMessage="Auto import sales paid with a credit account"
        />
    default:
        return key
    }
}


const renderConfigurationValues = (props: OwnProps) => {
    return props.configurationValues.map((c, i) => {
        return <ConfigurationValueComponent
            key={i}
            value={props.configuration[c.getKey()]}
            configurationValue={c}
            onChange={(newValue) => {
                const conf = props.configuration
                conf[c.getKey()] = newValue
                props.toggleSaveButtonStatus(isSaveEnabled(conf))
                props.onConfigurationChange(conf)
            }}
            renderConfigurationValueLabel={renderConfigurationValueLabel}
        />
    })
}

const renderSettingToggleLabels = (key: string): JSX.Element | string => {
    switch (key) {
    case VEND_FEATURE_SETTINGS.SyncManufacturerSku:
        return <FormattedMessage
            id="integration.form.edit.vend.sync_manufacturer_sku_label"
            defaultMessage="Sync manufacturer sku to supplier code"
        />
    default:
        return key
    }
}

const renderSettingToggles = (props: OwnProps): JSX.Element[] => {
    return Object.values(VEND_FEATURE_SETTINGS).map((featureKey, i: number) => {
        const isChecked = !!props.configuration[featureKey]
        return <FeatureToggleComponent
            key={i}
            featureKey={featureKey}
            checked={isChecked}
            onFeatureToggled={() => {
                const newConfig = { ...props.configuration, [featureKey]: !isChecked }
                props.onConfigurationChange(newConfig)
            }}
            renderFeatureLabel={renderSettingToggleLabels}
        />
    })
}

const renderConfigurationValueLabel = (key: string) => {
    switch (key) {
    case "bankTransferPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.vend.bank_transfer_payment_type_id"
            defaultMessage="Payment type for bank transfer"
        />
    case "cashPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.vend.cash_payment_type_id"
            defaultMessage="Payment type for cash"
        />
    case "creditCardPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.vend.credit_card_payment_type_id"
            defaultMessage="Payment type for credit card"
        />
    case "checkPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.vend.check_payment_type_id"
            defaultMessage="Payment type for cheque"
        />
    case "creditMemoPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.vend.credit_memo_payment_type_id"
            defaultMessage="Payment type for credit memo"
        />
    default:
        return key
    }
}


const isSaveEnabled = (configuration: object): boolean => {
    return !(Object.keys(configuration).filter(key =>
        configuration[key] === "" && !(["discountId", "coupon", "externalSubscriptionId"].includes(key))).length > 0
    )
}

export default injectIntl(VendFormComponent)


// WEBPACK FOOTER //
// ./src/integration/form/vend/VendFormComponent.tsx